/**
 * First-party profile traffic beacon (API: POST
 * /v1/public/:city/directory/specialist/:slug/traffic, migration 0028).
 *
 * Sends only an event name plus three coarse buckets — how the session
 * reached PulseBoard, whether this profile was the landing page, and the
 * audience — so specialists can be shown "N visits from Google search"
 * without PulseBoard storing anything that identifies a visitor.
 */
export type TrafficEvent =
  | "view"
  | "phone_click"
  | "website_click"
  | "email_click"
  | "referral_copy"
  | "referral_draft";
export type TrafficSource = "search" | "social" | "referral" | "direct" | "internal";
export type TrafficAudience = "public" | "clinician";

const BASE = import.meta.env.VITE_API_BASE_URL || "";
const ENTRY_KEY = "pb.entry";
const VIEWED_KEY = "pb.viewed";
const AUDIENCE_KEY = "pb.audience";

interface SessionEntry {
  source: Exclude<TrafficSource, "internal">;
  path: string;
}

const SEARCH_HOSTS = /(^|\.)(google|bing|duckduckgo|yahoo|ecosia|baidu|yandex|startpage|qwant|brave)\./i;
const SOCIAL_HOSTS = /(^|\.)(facebook|fb|instagram|linkedin|lnkd|t|x|twitter|reddit|youtube|threads)\.(com|co|in|net)$/i;

export function classifyReferrer(referrer: string, ownHost: string): SessionEntry["source"] | "internal" {
  if (!referrer) return "direct";
  let host: string;
  try {
    host = new URL(referrer).hostname;
  } catch {
    return "direct";
  }
  if (host === ownHost) return "internal";
  if (SEARCH_HOSTS.test(host)) return "search";
  if (SOCIAL_HOSTS.test(host)) return "social";
  return "referral";
}

function readSession<T>(key: string): T | null {
  try {
    const raw = window.sessionStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : null;
  } catch {
    return null;
  }
}

function writeSession(key: string, value: unknown): void {
  try {
    window.sessionStorage.setItem(key, JSON.stringify(value));
  } catch {
    // Private mode or blocked storage: counts degrade, the page does not.
  }
}

/**
 * Call once at boot, before the router changes the URL. Records how this
 * browser session reached PulseBoard and its landing path.
 */
export function captureSessionEntry(): void {
  if (typeof window === "undefined" || readSession<SessionEntry>(ENTRY_KEY)) return;
  const source = classifyReferrer(document.referrer, window.location.hostname);
  writeSession(ENTRY_KEY, {
    // A same-site referrer on a fresh session (new tab) still means the
    // visitor was already on PulseBoard; bucket it as direct.
    source: source === "internal" ? "direct" : source,
    path: normalisePath(window.location.pathname),
  } satisfies SessionEntry);
}

function normalisePath(pathname: string): string {
  return pathname.replace(/\/+$/, "") || "/";
}

/** GP mode toggle persists the audience choice for this browser. */
export function setAudiencePreference(audience: TrafficAudience): void {
  try {
    window.localStorage.setItem(AUDIENCE_KEY, audience);
  } catch {
    // ignore
  }
}

/** The visitor's explicit choice, or null if they have never toggled. */
export function getStoredAudience(): TrafficAudience | null {
  try {
    const value = window.localStorage.getItem(AUDIENCE_KEY);
    return value === "clinician" || value === "public" ? value : null;
  } catch {
    return null;
  }
}

export function getAudiencePreference(): TrafficAudience {
  return getStoredAudience() ?? "public";
}

export function sendProfileTraffic(
  citySlug: string,
  slug: string,
  event: TrafficEvent,
  options: { clinician?: boolean } = {},
): void {
  if (typeof window === "undefined" || !BASE) return;
  const path = `/directory/specialist/${slug}`;
  const entry = readSession<SessionEntry>(ENTRY_KEY);
  const landing = event === "view" && entry?.path === normalisePath(path);

  if (event === "view") {
    // One view per profile per browser session — refreshes and back/forward
    // navigation must not inflate a number shown to specialists.
    const viewed = readSession<string[]>(VIEWED_KEY) ?? [];
    if (viewed.includes(slug)) return;
    writeSession(VIEWED_KEY, [...viewed, slug]);
  }

  const body = JSON.stringify({
    event,
    // Session-level source: a view reached by clicking around after arriving
    // from Google still counts as search-sourced; `landing` separates the two.
    source: entry?.source ?? "direct",
    audience: options.clinician ? "clinician" : getAudiencePreference(),
    landing,
  });
  void fetch(`${BASE}/v1/public/${citySlug}/directory/specialist/${slug}/traffic`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    // Survives the page unloading after a website/phone click.
    keepalive: true,
    credentials: "omit",
  }).catch(() => {
    // Measurement must never interrupt the visitor.
  });
}
